/**
 * Derive an "active care plan" view from timeline events.
 *
 * Rules-based (no LLM): filter events with status === "active", group by
 * category in a clinically meaningful order, sort within each group by date
 * descending so the most recent entries show first.
 */

const CATEGORY_CONFIG = [
    { key: 'medication', label: 'Active Medications', shortLabel: 'Medication' },
    { key: 'procedure', label: 'Ongoing Procedures / Care', shortLabel: 'Procedure' },
    { key: 'surgery', label: 'Recent / Ongoing Surgical Care', shortLabel: 'Surgery' },
    { key: 'diagnosis', label: 'Active Diagnoses', shortLabel: 'Diagnosis' },
    { key: 'imaging', label: 'Pending / Active Imaging', shortLabel: 'Imaging' },
    { key: 'lab_result', label: 'Pending / Active Labs', shortLabel: 'Lab' },
    { key: 'encounter', label: 'Active Encounters', shortLabel: 'Encounter' },
    { key: 'hospital_course', label: 'Ongoing Hospital Course', shortLabel: 'Hospital Course' },
    { key: 'vital_signs', label: 'Active Vital Sign Monitoring', shortLabel: 'Vitals' },
];

export const ACTIVE_PLAN_CATEGORY_ORDER = CATEGORY_CONFIG.map((c) => c.key);
export const CATEGORY_LABELS = Object.fromEntries(CATEGORY_CONFIG.map((c) => [c.key, c.label]));
export const TIMELINE_CATEGORY_LABELS = Object.fromEntries(CATEGORY_CONFIG.map((c) => [c.key, c.shortLabel]));

/**
 * @param {Array<Object>} events - Timeline events as stored on the case doc.
 * @returns {Array<{category: string, label: string, events: Array<Object>}>}
 * Events with a missing category are grouped under 'unknown' and appended after
 * the ordered categories — they remain visible rather than being dropped.
 */
export function buildActiveCarePlan(events) {
    if (!Array.isArray(events) || events.length === 0) return [];

    const active = events.filter((e) => e && e.status === 'active');
    if (active.length === 0) return [];

    const byCategory = new Map();
    for (const event of active) {
        const cat = event.category || 'unknown';
        if (!byCategory.has(cat)) byCategory.set(cat, []);
        byCategory.get(cat).push(event);
    }

    for (const arr of byCategory.values()) {
        arr.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    }

    const groups = [];
    const seen = new Set();
    for (const cat of ACTIVE_PLAN_CATEGORY_ORDER) {
        if (byCategory.has(cat)) {
            groups.push({ category: cat, label: CATEGORY_LABELS[cat] || cat, events: byCategory.get(cat) });
            seen.add(cat);
        }
    }
    for (const [cat, evs] of byCategory.entries()) {
        if (!seen.has(cat)) {
            groups.push({ category: cat, label: CATEGORY_LABELS[cat] || cat, events: evs });
        }
    }
    return groups;
}
